import type { EventTypeView, UserEventView } from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { showToast } from '@crowdex/ui/components/toaster';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { type PluginPanel, railPanels } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { dockApp, dockAppIn } from '@/lib/desktop-shell/app-panels';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import type { AppPanelTarget, DesktopShellStore } from '@/lib/desktop-shell/store';
import { useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { electronInvoke, isElectronDesktop } from '@/lib/electron-bridge';
import { useLibraryPage } from '@/lib/navigation';
import {
  askForPushOnce,
  type OpenedNotification,
  openedNotification,
  syncPushSubscription,
} from '@/lib/push-notifications';
import {
  createNotificationFeed,
  type FeedNotification,
  type NotificationFeed,
  readable,
} from './notification-feed';

/**
 * Where a notification's `link` leads: its plugin's tab (`?item=<id>` selects an item,
 * `#/<route>` routes its view).
 */
export function notificationTarget(
  notification: Pick<UserEventView, 'plugin' | 'link'>,
  apps: readonly Pick<PluginPanel, 'view' | 'appView'>[],
): { view: PluginPanel['view']; target: AppPanelTarget } | null {
  if (notification.plugin === null || notification.link === null) return null;
  const app = apps.find((entry) => entry.appView?.plugin === notification.plugin);
  if (app === undefined) return null;
  let url: URL;
  try {
    url = new URL(notification.link, 'https://crowdex.invalid');
  } catch {
    // An app may send any string: one it cannot be opened at opens nothing.
    return null;
  }
  const route = url.hash.startsWith('#/') ? url.hash.slice(1) : undefined;
  return {
    view: app.view,
    target: { item: url.searchParams.get('item'), ...(route === undefined ? {} : { route }) },
  };
}

/** Whether the user is using this window: shown, and focused (not another app in front). */
function windowInUse(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

/** Whether the user is looking at a conversation right now: shown, in the window in use. */
function conversationOnScreen(store: DesktopShellStore, threadId: string): boolean {
  if (!windowInUse()) return false;
  const { chatId, splitThreadIds } = store.getState();
  return chatId === threadId || splitThreadIds.includes(threadId);
}

type Notifications = {
  notifications: readonly FeedNotification[];
  /** The types the user may hear of (the core's, their plugins', a linked cloud's), by id. */
  types: ReadonlyMap<string, EventTypeView>;
  /** The types whose events wait on the user (`waits`): Needs you reads them. */
  waitingTypes: ReadonlySet<string>;
  unread: number;
  /** Opens what it is about (its conversation, its app's tab), and marks it read. */
  open(notification: FeedNotification): void;
  /**
   * Marks these read (one opened from a list), or a conversation's; what waits on the user stays
   * waiting: reading is not answering.
   */
  markRead(target: readonly FeedNotification[] | { threadId: string }): void;
  /** Shows these no longer waiting: answered in place (their server settled them). */
  settle(target: readonly FeedNotification[]): void;
  markAllRead(): void;
  /** The last fetch failed: the list says so, with Retry (`refresh`). */
  loadFailed: boolean;
  /** Asks the server for the latest now (a list pulled down); settles once fetched. */
  refresh(): Promise<void>;
};

const NotificationsContext = createContext<Notifications | null>(null);

/** Dispatched on `window` once a notification opened its conversation (the phone shows it). */
export const NOTIFICATION_OPENED_EVENT = 'crowdex:notification-opened';

/** How long a conversation opened from a notification is waited for (the sidebar loading). */
const OPEN_RETRY_MS = 30_000;
const OPEN_RETRY_EVERY_MS = 500;
/** How long a clicked notification is looked for in the feed (loading) before its conversation opens. */
const CLICKED_LOOKUP_MS = 5_000;

/** A clicked notification the feed no longer lists: enough to open its conversation and read it. */
function clickedNotification(id: string, threadId: string): FeedNotification {
  return {
    id,
    source: '',
    plugin: null,
    type: '',
    title: '',
    body: null,
    severity: 'info',
    fields: {},
    link: null,
    url: null,
    threadId,
    group: null,
    delivery: 'native',
    createdAt: new Date().toISOString(),
    readAt: null,
  };
}

const NO_NOTIFICATIONS: readonly FeedNotification[] = [];

const NO_TYPES: readonly EventTypeView[] = [];

/** The types the user may hear of, as the settings list them. */
function loadEventTypes(): Promise<EventTypeView[]> {
  return apiFetch('/api/events/types', { cache: 'no-store' });
}

const NO_THREADS: ReadonlySet<string> = new Set();

/** A feed that lists what it is given and reads it in place (a preview's). */
function createStaticFeed(initial: readonly FeedNotification[]): NotificationFeed {
  let list = initial;
  let waitingTypes: ReadonlySet<string> = new Set();
  const listeners = new Set<() => void>();
  const read = (matches: (entry: FeedNotification) => boolean) => {
    const now = new Date().toISOString();
    list = list.map((entry) =>
      entry.readAt === null && matches(entry) ? { ...entry, readAt: now } : entry,
    );
    for (const listener of listeners) listener();
  };
  return {
    current: () => list,
    unreadThreadIds: () =>
      new Set(
        list.flatMap((entry) =>
          entry.readAt === null && entry.threadId !== null && readable(entry, waitingTypes)
            ? [entry.threadId]
            : [],
        ),
      ),
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    refresh: () => Promise.resolve(),
    markRead(target) {
      read(
        (entry) =>
          readable(entry, waitingTypes) &&
          (target === 'all' ||
            ('threadId' in target
              ? entry.threadId === target.threadId
              : target.some((given) => given.id === entry.id))),
      );
    },
    settle(target) {
      read((entry) => target.some((given) => given.id === entry.id));
    },
    setWaitingTypes(types) {
      waitingTypes = types;
    },
    failed: () => false,
    stop: () => listeners.clear(),
  };
}

const NO_FEED: NotificationFeed = {
  current: () => NO_NOTIFICATIONS,
  unreadThreadIds: () => NO_THREADS,
  subscribe: () => () => undefined,
  refresh: () => Promise.resolve(),
  markRead: () => undefined,
  settle: () => undefined,
  setWaitingTypes: () => undefined,
  failed: () => false,
  stop: () => undefined,
};

/**
 * The account's notifications (an agent replied or waits on the user, an automation failed):
 * each new one is shown once as a toast opening what it is about, unless its conversation is on
 * screen (then it is read at once); all of them are listed by the notifications button. A
 * machine's server lists and streams its linked Crowdex Cloud's too (what runs there).
 * System notifications are the desktop shell's, window open or not; a browser's are pushed to
 * its service worker, Crowdex open or not (`lib/push-notifications`).
 */
export function NotificationsProvider({
  children,
  enabled,
  preview,
  previewTypes,
}: {
  children: ReactNode;
  enabled: boolean;
  /** A preview's notifications (`/desktop-preview`): listed and read here only. */
  preview?: readonly FeedNotification[];
  /** The preview's event types (their titles, which wait). */
  previewTypes?: readonly EventTypeView[];
}) {
  const store = useDesktopShellStoreApi();
  const workbench = useWorkbench();
  const apps = useRef(railPanels(workbench));
  apps.current = railPanels(workbench);
  const library = useLibraryPage();
  const leaveLibraryPage = useRef(library.leave);
  leaveLibraryPage.current = library.leave;
  const opener = useRef(useConversationOpener());
  opener.current = useConversationOpener();
  const [feed, setFeed] = useState<NotificationFeed>(() =>
    preview === undefined || enabled ? NO_FEED : createStaticFeed(preview),
  );
  const feedRef = useRef(feed);
  feedRef.current = feed;

  const open = useRef((notification: FeedNotification) => {
    feedRef.current.markRead([notification]);
    leaveLibraryPage.current();
    const openApp = () => {
      // The cloud's link names its own workspace's item: only this server's open here.
      const target =
        notification.source === '' ? notificationTarget(notification, apps.current) : null;
      if (target !== null) dockApp(store, target.view, target.target);
      else if (notification.url !== null) window.open(notification.url, '_blank', 'noopener');
    };
    const threadId = notification.threadId;
    if (threadId === null) return openApp();
    // A window just opened from it may not list its conversations yet: tried again a while.
    const startedAt = Date.now();
    const attempt = () => {
      const current = opener.current;
      void (current === null ? Promise.resolve(false) : current.openConversation(threadId)).then(
        (opened) => {
          if (opened) {
            // About a conversation: what its link names docks in that conversation's panel.
            const target =
              notification.source === '' ? notificationTarget(notification, apps.current) : null;
            if (target !== null) dockAppIn(store, threadId, target.view, target.target);
            window.dispatchEvent(new Event(NOTIFICATION_OPENED_EVENT));
            return;
          }
          if (Date.now() - startedAt < OPEN_RETRY_MS) setTimeout(attempt, OPEN_RETRY_EVERY_MS);
          else openApp();
        },
      );
    };
    attempt();
  }).current;

  useEffect(() => {
    if (!enabled) return;
    const show = (notification: FeedNotification) => {
      if (notification.threadId !== null && conversationOnScreen(store, notification.threadId)) {
        feedRef.current.markRead([notification]);
        return;
      }
      // The user's settings of its type: listed only, or told of. The assistant ringing rings
      // in its own card (`IncomingCall`).
      if (notification.delivery !== 'toast' && notification.delivery !== 'native') return;
      if (notification.type === 'crowdex/assistant.ring') {
        if (notification.delivery === 'native') askForPushOnce();
        return;
      }
      const opens =
        notification.threadId !== null ||
        notification.url !== null ||
        (notification.source === '' && notificationTarget(notification, apps.current) !== null);
      const onOpen = opens ? () => open(notification) : undefined;
      showToast({
        title: notification.title,
        ...(notification.body ? { description: notification.body } : {}),
        tone: notification.severity,
        ...(onOpen ? { action: { label: 'Open', onSelect: onOpen } } : {}),
        // A request waiting on the user (an approval) stays until they open or dismiss it.
        ...(notification.severity === 'warning'
          ? { duration: Number.POSITIVE_INFINITY }
          : notification.severity === 'error'
            ? { duration: 30_000 }
            : {}),
      });
      // A browser's system notifications are pushed by the server to its service worker.
      if (notification.delivery === 'native') askForPushOnce();
    };
    const created = createNotificationFeed(show);
    feedRef.current = created;
    setFeed(created);
    return () => {
      created.stop();
      feedRef.current = NO_FEED;
      setFeed(NO_FEED);
    };
  }, [enabled, open, store]);

  // Desktop: which system notification was clicked (it may have opened this window).
  useEffect(() => {
    if (!enabled || !isElectronDesktop()) return;
    const openClicked = () =>
      void electronInvoke<FeedNotification | null>('notification_take')
        .then((clicked) => {
          if (clicked !== null) open(clicked);
        })
        .catch(() => undefined);
    openClicked();
    const stops = [window.crowdexDesktop?.listen('notification-open', openClicked)];
    return () => {
      for (const stop of stops) stop?.();
    };
  }, [enabled, open]);

  // Browser: the server pushes system notifications here; a click on one opens its event, in
  // this window (the service worker's message) or one it opened (`?notification=<id>`).
  useEffect(() => {
    if (!enabled || isElectronDesktop()) return;
    syncPushSubscription();
    // The app lists the latest notifications only: one older than them still opens its
    // conversation (and is read), from what the push named.
    const openClicked = ({ id, threadId }: OpenedNotification) => {
      const startedAt = Date.now();
      const attempt = () => {
        // The feed loads after this runs: the current one is read at each try.
        const found = feedRef.current
          .current()
          .find((entry) => entry.source === '' && entry.id === id);
        if (found !== undefined) open(found);
        else if (Date.now() - startedAt < CLICKED_LOOKUP_MS)
          setTimeout(attempt, OPEN_RETRY_EVERY_MS);
        else if (threadId !== null) open(clickedNotification(id, threadId));
      };
      attempt();
    };
    const parameters = new URLSearchParams(window.location.search);
    const clicked = parameters.get('notification');
    if (clicked !== null) {
      const threadId = parameters.get('thread');
      parameters.delete('notification');
      parameters.delete('thread');
      const query = parameters.toString();
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${query === '' ? '' : `?${query}`}${window.location.hash}`,
      );
      openClicked({ id: clicked, threadId });
    }
    const onMessage = (event: MessageEvent) => {
      const opened = openedNotification(event.data);
      if (opened !== null) openClicked(opened);
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onMessage);
  }, [enabled, open]);

  // The sidebar's unread conversations are those with unread notifications, on every device.
  useEffect(() => {
    if (!enabled) return;
    const follow = () => {
      const unread = feed.unreadThreadIds();
      store
        .getState()
        .setUnreadThreadIds((current) =>
          current.size === unread.size && [...unread].every((id) => current.has(id))
            ? current
            : unread,
        );
    };
    follow();
    return feed.subscribe(follow);
  }, [enabled, feed, store]);

  // Opening a conversation reads what was told about it.
  useEffect(() => {
    if (!enabled) return;
    const readShown = () => {
      if (!windowInUse()) return;
      const { chatId, splitThreadIds } = store.getState();
      for (const threadId of [chatId, ...splitThreadIds]) {
        if (feed.unreadThreadIds().has(threadId)) feed.markRead({ threadId });
      }
    };
    readShown();
    const unsubscribeStore = store.subscribe((state, previous) => {
      if (state.chatId !== previous.chatId || state.splitThreadIds !== previous.splitThreadIds)
        readShown();
    });
    const unsubscribeFeed = feed.subscribe(readShown);
    document.addEventListener('visibilitychange', readShown);
    window.addEventListener('focus', readShown);
    return () => {
      unsubscribeStore();
      unsubscribeFeed();
      document.removeEventListener('visibilitychange', readShown);
      window.removeEventListener('focus', readShown);
    };
  }, [enabled, feed, store]);

  const notifications = useSyncExternalStore(feed.subscribe, feed.current, feed.current);

  // The types: loaded once, and again when an event of one this page does not know comes (a
  // plugin installed since). A preview's are given.
  const [loadedTypes, setLoadedTypes] = useState<readonly EventTypeView[]>(
    () => previewTypes ?? NO_TYPES,
  );
  // What the last load listed, and the types asked for since: null until loaded once.
  const [asked, setAsked] = useState<ReadonlySet<string> | null>(null);
  const unknownType =
    asked === null
      ? undefined
      : notifications.find(
          (notification) => notification.type !== '' && !asked.has(notification.type),
        )?.type;
  useEffect(() => {
    if (!enabled || (asked !== null && unknownType === undefined)) return;
    let live = true;
    // Asked once per type it does not list: an event of a type gone stays without a title.
    const settle = (listed: readonly EventTypeView[] | null) => {
      if (!live) return;
      if (listed !== null) setLoadedTypes(listed);
      setAsked((current) => {
        if (listed === null && current === null) return null;
        return new Set([
          ...(current ?? []),
          ...(listed ?? []).map((type) => type.type),
          ...(unknownType === undefined ? [] : [unknownType]),
        ]);
      });
    };
    void loadEventTypes().then(settle, () => settle(null));
    return () => {
      live = false;
    };
    // `asked` changes only once a load settled: what it adds is what the next one asks.
  }, [enabled, unknownType, asked]);
  const types = useMemo(() => new Map(loadedTypes.map((type) => [type.type, type])), [loadedTypes]);
  const waitingTypes = useMemo(
    () => new Set(loadedTypes.filter((type) => type.waits).map((type) => type.type)),
    [loadedTypes],
  );
  // The feed leaves what waits on the user to its answer: told before anything reads.
  feed.setWaitingTypes(waitingTypes);
  const failed = useSyncExternalStore(feed.subscribe, feed.failed, feed.failed);
  const value: Notifications = {
    notifications,
    types,
    waitingTypes,
    // News only: what waits on the user is in Needs you, unread until answered.
    unread: notifications.filter(
      (notification) => notification.readAt === null && readable(notification, waitingTypes),
    ).length,
    open,
    markRead: (target) => feed.markRead(target),
    settle: (target) => feed.settle(target),
    markAllRead: () => feed.markRead('all'),
    loadFailed: failed,
    refresh: () => feed.refresh(),
  };
  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications(): Notifications | null {
  return useContext(NotificationsContext);
}
